import * as React from 'react';
import {Button, Stack, styled, Switch, Tabs, Tab, Box, Typography, Fab, Grid, useTheme} from '@mui/material'; // Container,IconButton,
import {ProcessTabs} from 'app/types/Shared';
import {Add, Remove, SubdirectoryArrowRight} from '@mui/icons-material';
import {useSelector} from 'react-redux';
import {selectSelectedProcess} from 'app/store/appState/appStateSlice';
import themes from 'themes';

const StyledSwtich = styled(Switch)({
  width: 36,
  height: 18,
  padding: 0,
  margin: 2,
  '& .MuiSwitch-switchBase': {
    padding: 1,
    margin: 1,
    transitionDuration: '200ms',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: themes().palette.primary.main,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: themes().palette.mode === 'light' ? themes().palette.grey[100] : themes().palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: themes().palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 14,
    height: 14,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    border: 'solid 1px lightgrey',
    backgroundColor: themes().palette.grey[500],
    opacity: 1,
    transition: themes().transitions.create(['background-color'], {
      duration: 300,
    }),
  },
});

type keyVal = {
  key: number;
  label: string;
};

type leveledData = {
  title: string;
  order: number;
  l2: Array<keyVal>;
};

export interface FlowHeaderProps {
  data?: Array<leveledData>;
  selectedL1Label: string;
  selectedL2Label: string;
  onL1Click: (subprocess: number, label: string) => void;
  onL2Click: (id: string) => void;
  children?: React.ReactNode;
  storybook?: boolean;
  mode: ProcessTabs;
  zoomIn?: () => void;
  zoomOut?: () => void;
  zoomLevel?: number;
  simplifiedCallback?: (arg0: boolean) => void;
  simplifiedRACI?: boolean;
}

const SubProcessNavigation = ({
  data,
  onL1Click,
  onL2Click,
  selectedL1Label,
  selectedL2Label,
  children,
  storybook,
  mode,
  zoomIn,
  zoomOut,
  zoomLevel,
  simplifiedRACI = false,
  simplifiedCallback,
}: FlowHeaderProps) => {
  const theme = useTheme();
  const l1Ref = React.useRef(selectedL1Label);
  const selectedProcess = useSelector(selectSelectedProcess);

  const getInitial = () => {
    if (l1Ref.current !== selectedL1Label) {
      l1Ref.current = selectedL1Label;
      return 0;
    } else {
      const startL1 = data?.find((x) => x.title === selectedL1Label);
      if (startL1) {
        const startL2 = startL1.l2.findIndex((x) => x.label === selectedL2Label);
        if (startL2 !== -1) {
          return startL2;
        }
      }
    }

    return 0;
  };

  const [value, setValue] = React.useState(getInitial());
  const [l1, setl1] = React.useState(selectedL1Label);

  // //switching between process_map and raci
  // const prevMode = React.useRef(mode);

  //For L1 inital change
  React.useEffect(() => {
    const startL1 = data?.find((x) => x.title === selectedL1Label);
    if (!startL1) {
      const startL1 = data?.at(0);
      if (startL1) {
        onL1Click(startL1.order, startL1.title);
        setValue(0);
      }
    } else {
      const l2Label = startL1.l2.at(value)?.label;
      if (l2Label && (!selectedL2Label || (selectedL2Label && selectedL2Label.at(0) != l2Label.at(0)))) {
        const startL2 = startL1.l2.at(0);
        if (startL2) {
          onL2Click(startL2.label);
        }
      } else {
        const startL2 = startL1.l2.findIndex((x) => x.label === selectedL2Label);
        if (startL2 !== -1 && startL2 !== value) {
          setValue(startL2);
        }
      }
    }
    //prevMode.current = mode;
  }, [data, onL1Click, onL2Click, selectedL1Label, selectedL2Label, value]);

  //on L1 Change. Scroll map back to beggining then change L1 and set L2 to first one in new L1 data
  const setNextL1 = (order: number, title: string) => {
    onL1Click(order, title);
    setValue(0);
  };

  //When L2 Tab Changes
  const handleChange = (event: React.SyntheticEvent, newVal: number) => {
    if (event) setValue(newVal);
  };

  //variables for storybook(l1) or prod(selectedL1Label)
  const getVariable = () => {
    if (storybook) {
      return l1;
    } else {
      return selectedL1Label;
    }
  };

  const zoomLevelPercentage = (zoom?: number) => {
    if (zoom) {
      const zoomRounded = (Math.round(zoom * 10) / 10).toFixed(1);
      switch (zoomRounded) {
        case '0.2':
          return '20%';
        case '0.4':
          return '40%';
        case '0.6':
          return '60%';
        case '0.8':
          return '80%';
        case '1.0':
          return '100%';
        case '1.2':
          return '120%';
        case '1.4':
          return '140%';
        case '1.6':
          return '160%';
        case '1.8':
          return '180%';
        case '2.0':
          return '200%';
        default:
          return 'No Zoom';
      }
    }
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    simplifiedCallback && simplifiedCallback(event.target.checked);
    if (!event.target.checked) {
      setTimeout(() => {
        onL2Click(selectedL2Label);
      }, 200);
    }
  };

  const findLength = () => {
    const length = data?.find((p) => p.title === getVariable())?.l2?.length;
    if (length && length > 0) {
      return length - 1;
    }
  };

  return (
    <>
      <Grid
        container
        direction='row'
        sx={{
          bgcolor: 'white',
          boxShadow: '0px 2px 5px 0px rgba(255, 255, 255, 0.15)',
          border: '1px solid rgba(217, 217, 217, 1)',
        }}
      >
        <Grid item xs={10.5}>
          <Stack direction='row' px={6}>
            {data?.map((x, index) => (
              <Button
                key={`${x.order}--${x.title}.${index}`}
                onClick={() => {
                  setNextL1(x.order, x.title);
                  if (storybook) setl1(x.title);
                }}
                disableTouchRipple
                sx={{
                  borderRadius: 0,
                  opacity: getVariable() === x.title ? 'none' : 0.8,
                  color: getVariable() === x.title ? 'primary.main' : 'grey.800',
                  ':hover': {
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.primary.main,
                    opacity: 'none',
                  },
                  py: 1,
                  my: 0.5,
                  borderRight: index !== data?.length - 1 ? `1px solid ${theme.palette.grey[600]}` : 'none',
                  pr: index !== data?.length - 1 ? '20px' : 0,
                  pl: index !== 0 ? '20px' : 0,
                  maxWidth: '110%',
                  textAlign: 'left',
                }}
              >
                <Typography variant='h5'>{x.title}</Typography>
              </Button>
            ))}
          </Stack>
        </Grid>
        {mode === ProcessTabs.Map && !!data?.length && (
          <Grid item xs={1.5}>
            <Stack
              direction='row'
              spacing={0.5}
              alignItems='center'
              flexGrow={1}
              height='100%'
              justifyContent='flex-end'
              sx={{pr: 4}}
            >
              <Fab
                disabled={zoomLevel && zoomLevel < 0.39 ? true : false}
                variant='circular'
                size='small'
                sx={{
                  backgroundColor: theme.palette.grey[800],
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'grey.800',
                  },
                  height: '24px',
                  width: '36px',
                  zIndex: 40,
                }}
                onClick={() => zoomOut && zoomOut()}
              >
                <Remove />
              </Fab>
              <Fab
                disabled={zoomLevel && zoomLevel > 1.81 ? true : false}
                variant='circular'
                size='small'
                sx={{
                  backgroundColor: theme.palette.grey[800],
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'grey.800',
                  },
                  height: '24px',
                  width: '36px',
                  zIndex: 40,
                }}
                onClick={() => zoomIn && zoomIn()}
              >
                <Add />
              </Fab>
              <Typography variant='h6' px={0.5}>
                {zoomLevelPercentage(zoomLevel)}
              </Typography>
            </Stack>
          </Grid>
        )}
        {mode === ProcessTabs.RACI && selectedProcess?.simplified_raci && (
          <Grid item xs={1.5}>
            <Stack direction='row' alignItems='center' height='100%' spacing={1}>
              <Typography variant='h6'>Simplified RACI</Typography>
              <StyledSwtich onChange={handleSwitchChange} checked={simplifiedRACI} />
            </Stack>
          </Grid>
        )}
      </Grid>

      {data?.find((p) => p.title === getVariable())?.l2.length ? (
        <Box
          bgcolor={theme.palette.grey[100]}
          display='flex'
          flexDirection='row'
          borderBottom={`1px solid ${theme.palette.grey[500]}`}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            variant='scrollable'
            scrollButtons
            textColor='secondary'
            TabIndicatorProps={{
              style: {display: 'none'},
            }}
            TabScrollButtonProps={{
              style: {color: 'black', paddingLeft: 3, paddingRight: 3},
            }}
            sx={{
              overflow: 'hidden',
              pl: '10px',
            }}
          >
            {data.find((p) => p.title === getVariable()) &&
              data
                .find((p) => p.title === getVariable())
                ?.l2.map((item, index) => (
                  <Tab
                    label={
                      <Box textAlign='left'>
                        <Stack direction='row' spacing='10px' alignItems='center'>
                          <Typography
                            variant='h6'
                            color={value === index ? 'primary' : 'grey.800'}
                            fontWeight={value === index ? '600' : '400'}
                            sx={{
                              ':hover': {
                                color: theme.palette.primary.main,
                              },
                              textTransform: 'none',
                            }}
                          >
                            {item.label}
                          </Typography>
                          {findLength() !== index && (
                            <SubdirectoryArrowRight
                              fontSize='small'
                              sx={{
                                color: value === index ? 'primary.main' : 'grey.800',
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    }
                    sx={{
                      padding: 0,
                      pr: '40px',
                      maxWidth: 'none',
                    }}
                    key={`${item.key}--${item.label}.${index}`}
                    disableRipple
                    id={`tab-${item.label}`}
                    onClick={() => {
                      onL2Click(item.label);
                    }}
                  />
                ))}
          </Tabs>
        </Box>
      ) : (
        <></>
      )}
      {children && children}
    </>
  );
};

export default SubProcessNavigation;
